import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { refreshTokenWithSessionId, setLocalToken } from '../../services/authService';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  margin: 0 0 10px 0;
  color: #333;
  font-size: 24px;
`;

const Subtitle = styled.p`
  margin: 0 0 20px 0;
  color: #666;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  max-height: 400px;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  resize: vertical;
  box-sizing: border-box;
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #007bff;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #6c757d;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #545b62;
  }
`;

const ClearButton = styled(Button)`
  background-color: #dc3545;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #c82333;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
`;

const SuccessMessage = styled.div`
  color: #155724;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
`;

const InfoBox = styled.div`
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #004085;
`;

const CharCount = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 5px;
  text-align: right;
`;

const DebugInfo = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 10px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  max-height: 100px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

const LocalTokenInput = ({ onTokenSubmit, currentToken }) => {
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // No need to set current token since we're using sessionId now
  }, [currentToken]);

  const validateSessionId = (sessionIdString) => {
    setDebugInfo(''); // Clear previous debug info
    
    if (!sessionIdString || sessionIdString.trim() === '') {
      setDebugInfo('Session ID is empty');
      return 'Session ID cannot be empty';
    }

    const cleanSessionId = sessionIdString.trim();
    
    setDebugInfo(`Session ID length: ${cleanSessionId.length}`);

    // Basic validation - session IDs are typically alphanumeric strings
    if (cleanSessionId.length < 10) {
      setDebugInfo(prev => prev + '\n\nERROR: Session ID seems too short');
      return 'Session ID appears to be invalid (too short)';
    }

    setDebugInfo(prev => prev + '\n\nSession ID format looks valid');
    return null;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    // Clean the session ID by removing whitespace, newlines, etc.
    const cleanedSessionId = sessionId.trim().replace(/[\r\n\s]/g, '');
    
    console.log("cleanedSessionId", cleanedSessionId);
    
    const validationError = validateSessionId(cleanedSessionId);

    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      setDebugInfo(prev => prev + '\n\nCalling refreshTokenWithSessionId...');
      
      // Call refreshTokenWithSessionId to get the JWT token
      const response = await refreshTokenWithSessionId(cleanedSessionId);
      
      console.log("Response from refreshTokenWithSessionId:", response);
      
      const jwtToken = response.data && response.data.jwtToken ? response.data.jwtToken : null;
      
      if (!jwtToken) {
        setDebugInfo(prev => prev + '\n\nERROR: No JWT token received from server');
        setError('Failed to get JWT token from session ID. Please check if the session ID is valid.');
        setIsLoading(false);
        return;
      }

      setDebugInfo(prev => prev + `\n\nJWT token received (length: ${jwtToken.length})`);
      
      // Store the JWT token as dev token
      setLocalToken(jwtToken);
      
      setDebugInfo(prev => prev + '\n\nToken stored successfully!');
      setSuccess('Session ID validated and token retrieved successfully! The page will reload to apply the token.');
      
      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Error getting token from session ID:', err);
      setDebugInfo(prev => prev + `\n\nERROR: ${err.message || 'Unknown error'}`);
      setError('Failed to get token from session ID: ' + (err.response?.data?.message || err.message || 'Unknown error'));
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setSessionId('');
    setError('');
    setSuccess('');
    setDebugInfo('');
    setLocalToken(null); // Clear the dev token
    setSuccess('Token cleared successfully! The page will reload.');
    
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleSkip = () => {
    // Redirect to test environment for authentication
    const loginUrl = process.env.REACT_APP_TARGET_RETURN_URL || 'https://myaccount-tst.qualcomm.com/profile';
    window.location.href = loginUrl;
  };

  return (
    <Overlay>
      <Modal>
        <Title><span role="img" aria-label="wrench">🔧</span> Local Token Input</Title>
        <Subtitle>For local development only - Enter your Session ID</Subtitle>
        
        <InfoBox>
          <strong>Localhost Development Mode</strong>
          <p style={{ margin: '8px 0 0 0' }}>
            You're running on localhost without a session cookie. Please perform below two steps:
          </p>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li><strong>Step 1:</strong> Click "Login to Test" to authenticate normally</li>
            <li><strong>Step 2:</strong> Enter the  Session ID (QID_TST_SESSION) below to set JWT token</li>
          </ul>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#004085' }}>
            <span role="img" aria-label="lightbulb">💡</span> The Session ID will be used to fetch your JWT token from the server automatically.
          </p>
        </InfoBox>

        <TextArea
          value={sessionId}
          onChange={(e) => {
            setSessionId(e.target.value);
            setError('');
            setSuccess('');
            setDebugInfo('');
          }}
          placeholder="After completing Step 1, get the value of QID_TST_SESSION from Cookies and Paste it here (e.g., ZsZ0rwJ0fszs8A1P3JsQ0nOjXO8Vket4)&#10;&#10;"
          autoFocus
          spellCheck={false}
          maxLength={500}
        />

        <CharCount>
          {sessionId.length > 0 ? `${sessionId.length} characters` : 'No session ID entered'}
        </CharCount>

        {debugInfo && <DebugInfo>{debugInfo}</DebugInfo>}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <ButtonGroup>
          {currentToken && (
            <ClearButton onClick={handleClear} disabled={!!success}>
              Clear Token
            </ClearButton>
          )}
          <SecondaryButton onClick={handleSkip} disabled={!!success || isLoading}>
            Login to Test
          </SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={!sessionId.trim() || !!success || isLoading}>
            {isLoading ? 'Fetching Token...' : 'Apply Session ID'}
          </PrimaryButton>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default LocalTokenInput;